/**
 * System Preferences API service for managing organization-wide settings.
 * @module lib/api/system-preferences.api
 */

import { apiClient } from './api-client';
import { createSingletonApi } from './singleton-factory';

export interface SystemPreferenceValue {
  id: string;
  systemPreferenceDefinitionId: string;
  organizationId: string;
  customValue: unknown;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

class SystemPreferenceApiService {
  /**
   * Gets a system preference by reference key
   * @param referenceKey - The preference reference key
   * @returns The preference value or null if not found
   */
  async getPreference(referenceKey: string): Promise<SystemPreferenceValue | null> {
    try {
      const encodedKey = encodeURIComponent(referenceKey);
      const response = await apiClient<{ success: boolean; data: SystemPreferenceValue }>(
        `/system-preferences/${encodedKey}`
      );
      return response.data;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        const err = error as { response?: { status?: number } };
        if (err.response?.status === 404) {
          return null;
        }
      }
      throw error;
    }
  }

  /**
   * Gets all system preferences for the organization
   * @returns List of all preference values
   */
  async getAllPreferences(): Promise<SystemPreferenceValue[]> {
    const response = await apiClient<{ data: SystemPreferenceValue[] }>('/system-preferences');
    return response.data;
  }

  /**
   * Updates a system preference value
   * @param referenceKey - The preference reference key
   * @param value - The new value
   * @returns The updated preference
   */
  async updatePreference(referenceKey: string, value: unknown): Promise<SystemPreferenceValue> {
    const response = await apiClient<{ data: SystemPreferenceValue }>(
      `/system-preferences/${referenceKey}`,
      {
        method: 'PUT',
        body: { customValue: value },
      }
    );
    return response.data;
  }

  /**
   * Creates a new system preference
   * @param referenceKey - The preference reference key
   * @param value - The preference value
   * @returns The created preference
   */
  async createPreference(referenceKey: string, value: unknown): Promise<SystemPreferenceValue> {
    const response = await apiClient<{ data: SystemPreferenceValue }>('/system-preferences', {
      method: 'POST',
      body: { referenceKey, customValue: value },
    });
    return response.data;
  }

  /**
   * Deletes a system preference
   * @param referenceKey - The preference reference key
   */
  async deletePreference(referenceKey: string): Promise<void> {
    await apiClient(`/system-preferences/${referenceKey}`, {
      method: 'DELETE',
    });
  }
}

export const systemPreferenceApi = createSingletonApi(SystemPreferenceApiService);
